import { MathHelper } from "@/Common/Helpers/MathHelper";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { CreatinineClearanceAlgorithmLookup } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LabConcentrationUnitConverter } from "@/Common/Utilities/Units/LabConcentrationUnitConverter";
import { SerumCreatinineConcentrationUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { VitalSign } from "@/Patient/Models/VitalSigns/VitalSign";

class MedicalCalculator {
    public static calculateBodySurface(weightInKilogram: number, heightInCentimeter: number): number {
        if (!weightInKilogram || !heightInCentimeter)
            return null;

        return Math.pow(((weightInKilogram * heightInCentimeter) / 3600), 1.0 / 2.0);
    }

    public static calculateBodyMassIndex(weightInKilogram: number, heightInMeter: number): number {
        if (!weightInKilogram || !heightInMeter)
            return null;

        return weightInKilogram / Math.pow(heightInMeter, 2);
    }

    /**
     * Calculate the creatinine clearance for the given patient vital sign.
     * @param gender The patient's sex at birth.
     * @param birthDate The patient's birthdate.
     * @param vitalSign The vital sign.
     * @returns The creatinine clearance value, or null if there is insufficient data to calculate the clearance.
     */
    public static calculateCreatinineClearance(
        gender: Gender,
        birthDate: Date,
        vitalSign: VitalSign | MedicalItemVitalSign
    ): number | null {
        let ageInYears = LuxonDateTimeParser.parse(birthDate)?.diffNow(["year", "month"])?.years;
        if (ageInYears < 0)
            ageInYears = Math.abs(ageInYears);

        const {
            creatinineClearanceAlgorithmLookupId,
            serumCreatinine,
            serumCreatinineUnit
        } = MedicalCalculator.getVitalSignCreatinineInfo(vitalSign);

        if (
            !birthDate
            || IsNullOrUndefined(gender)
            || gender === Gender.Unspecified
            || !serumCreatinine
        ) {
            return null;
        }

        const serumCreatinineConcentration = LabConcentrationUnitConverter.convertSerumCreatinineConcentrationTo(
            serumCreatinineUnit,
            SerumCreatinineConcentrationUnit.milligramsPer100millilitre,
            serumCreatinine
        );

        const isFemale = gender === Gender.Female;

        const algorithmLookupCode = cacheStore.GetLookupById(creatinineClearanceAlgorithmLookupId)?.LookupCode;
        let result: number = null;
        switch (algorithmLookupCode) {
            case CreatinineClearanceAlgorithmLookup.CockcroftGault: {
                const weightInKg = vitalSign.getWeightIn(WeightUnit.Kilogram);
                if (!weightInKg)
                    return null;

                result = MedicalCalculator.calculateCockcroftGaultCreatinineClearance(
                    isFemale,
                    ageInYears,
                    weightInKg,
                    serumCreatinineConcentration
                );
                break;
            }

            case CreatinineClearanceAlgorithmLookup.MDRD:
            case CreatinineClearanceAlgorithmLookup.MDRD175: {
                result = MedicalCalculator.calculateMdrdCreatinineClearance(
                    isFemale,
                    ageInYears,
                    serumCreatinineConcentration,
                    algorithmLookupCode === CreatinineClearanceAlgorithmLookup.MDRD175
                );
                break;
            }

            case CreatinineClearanceAlgorithmLookup.CKDEPI:
            case CreatinineClearanceAlgorithmLookup.CKD_EPI: {
                result = MedicalCalculator.calculateChkEpiCreatinineClearance(
                    isFemale,
                    ageInYears,
                    serumCreatinineConcentration
                );
                break;
            }
        }

        return result && Number.isFinite(result)
            ? MathHelper.roundToDecimals(result, 3)
            : null;
    }

    /**
     * Get the creatinine info from either a {@link VitalSign} or {@link MedicalItemVitalSign}.
     * @param vitalSign The vital sign.
     * @returns A tuple of the algorithm lookup ID, the serum creatinine concentration value and unit.
     */
    private static getVitalSignCreatinineInfo(vitalSign: VitalSign | MedicalItemVitalSign): {
        creatinineClearanceAlgorithmLookupId?: string,
        serumCreatinine?: number,
        serumCreatinineUnit?: SerumCreatinineConcentrationUnit
    } {
        if (vitalSign instanceof VitalSign) {
            return {
                creatinineClearanceAlgorithmLookupId: vitalSign.creatineClearanceAlgorithmLookupId,
                serumCreatinine: vitalSign.serumCreatinine,
                serumCreatinineUnit: vitalSign.serumCreatinineConcentrationUnit
            };
        }

        if (vitalSign instanceof MedicalItemVitalSign) {
            return {
                creatinineClearanceAlgorithmLookupId: vitalSign.CreatinineClearanceAlgorythmLookupId,
                serumCreatinine: vitalSign.SerumCreatinine,
                serumCreatinineUnit: vitalSign.SerumCreatinineUnit
            };
        }

        return {};
    }

    /**
     * Calculate creatinine clearance using the CHK-EPI 2021 formula.
     * @param isFemale Whether the patient is female.
     * @param ageInYears The patient's age, in years.
     * @param weightInKg The patient's weight, in kilograms.
     * @param serumCreatinineConcentration The serum creatinine concentration, in milligrams per 100 mililiter (mg/dL).
     * @see https://en.wikipedia.org/wiki/Glomerular_filtration_rate#Cockcroft-Gault_formula
     */
    private static calculateCockcroftGaultCreatinineClearance(
        isFemale: boolean,
        ageInYears: number,
        weightInKg: number,
        serumCreatinineConcentration: number
    ): number {
        // (140 - Age) x Mass (in kilograms) x [0.85 if Female]
        // ----------------------------------------------------
        //           72 x Serum Creatinine (in mg/dL)

        const sexRatio = isFemale ? 0.85 : 1;

        return ((140 - ageInYears) * weightInKg * sexRatio) / (72 * serumCreatinineConcentration);
    }

    /**
     * Calculate creatinine clearance using the MDRD formula.
     * @param isFemale Whether the patient is female.
     * @param ageInYears The patient's age, in years.
     * @param serumCreatinineConcentration The serum creatinine concentration, in milligrams per 100 mililiter (mg/dL).
     * @param applyIdmsAdjustment Whether the IDMS serum adjustment should be applied.
     * @see https://en.wikipedia.org/wiki/Glomerular_filtration_rate#Modification_of_diet_in_renal_disease_(MDRD)_formula
     */
    private static calculateMdrdCreatinineClearance(
        isFemale: boolean,
        ageInYears: number,
        serumCreatinineConcentration: number,
        applyIdmsAdjustment: boolean
    ): number {
        // For creatinine in mg/dL: 186 x [Serum Creatinine]^-1.154 x Age^-0.203 x [0.742 if Female]
        // note: x [1.212 if Black] is intentionally omitted; ethnicity-based adjustments are discouraged

        const sexRatio = isFemale ? 0.742 : 1;
        const idmsAdjustment = applyIdmsAdjustment ? (175 / 186) : 1; // IDMS-calibrated serum creatinine is about 6% lower

        return (
            186
            * Math.pow(serumCreatinineConcentration, -1.154)
            * Math.pow(ageInYears, -0.203)
            * sexRatio
            * idmsAdjustment
        );
    }

    /**
     * Calculate creatinine clearance using the CHK-EPI 2021 formula.
     * @param isFemale Whether the patient is female.
     * @param ageInYears The patient's age, in years.
     * @param serumCreatinineConcentration The serum creatinine concentration, in milligrams per 100 mililiter (mg/dL).
     * @see https://en.wikipedia.org/wiki/Glomerular_filtration_rate#CKD-EPI_formula
     */
    private static calculateChkEpiCreatinineClearance(
        isFemale: boolean,
        ageInYears: number,
        serumCreatinineConcentration: number
    ): number {
        // 142 x min(SCr/k, 1)^a x max(SCr/k, 1)^-1.209 x 0.9938^age x [1.012 if Female]
        // where k is 0.7 for females and 0.9 for males and a is −0.241 for females and −0.302 for males

        const k = isFemale ? 0.7 : 0.9;
        const a = isFemale ? -0.241 : -0.302;
        const sexRatio = isFemale ? 1.012 : 1;

        return (
            142
            * Math.pow(Math.min(serumCreatinineConcentration / k, 1), a)
            * Math.pow(Math.max(serumCreatinineConcentration / k, 1), -1.209)
            * Math.pow(0.9938, ageInYears)
            * sexRatio
        );
    }
}

export { MedicalCalculator };
